// src/api/proveedores.js - API para manejo de proveedores
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

/**
 * Obtener todos los proveedores
 * @param {string} estado - 'all', 'activos', 'inactivos', 'activo', 'inactivo'
 * @returns {Promise} Lista de proveedores
 */
export const obtenerProveedores = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo proveedores...', { estado });
    
    let endpoint = '/api/Proveedor';
    
    // Usar endpoint específico según el estado
    if (estado === 'activos') {
      endpoint = '/api/Proveedor/activos';
    } else if (estado === 'inactivos' || estado === 'inactivo') {
      endpoint = '/api/Proveedor/estado/inactivo';
    } else if (estado === 'activo') {
      endpoint = '/api/Proveedor/estado/activo';
    }
    
    const response = await apiFetch(endpoint);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Proveedores obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo proveedores:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un proveedor por ID
 * @param {number} id - ID del proveedor
 * @returns {Promise} Datos del proveedor
 */
export const obtenerProveedorPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de proveedor es requerido');
    }
    
    console.log(`🔍 Obteniendo proveedor ID: ${id}`);
    
    const response = await apiFetch(`/api/Proveedor/${id}`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Proveedor obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo proveedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener proveedor con relaciones por ID
 * @param {number} id - ID del proveedor
 * @returns {Promise} Datos del proveedor con relaciones
 */
export const obtenerProveedorConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de proveedor es requerido');
    }
    
    console.log(`🔍 Obteniendo proveedor con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/Proveedor/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Proveedor con relaciones obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo proveedor con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un RIF ya existe
 * @param {string} rif - RIF a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarRifProveedor = async (rif) => {
  try {
    if (!rif || rif.trim() === '') {
      throw new Error('RIF es requerido');
    }
    
    console.log(`🔎 Verificando RIF de proveedor: "${rif}"`);
    
    const response = await apiFetch(`/api/Proveedor/rif/${encodeURIComponent(rif)}/exists`);
    
    return {
      success: true,
      existe: response.data || false,
      message: response.message || (response.data ? 'El RIF ya existe' : 'RIF disponible')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando RIF "${rif}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si un proveedor existe por ID
 * @param {number} id - ID del proveedor
 * @returns {Promise} Resultado de la verificación
 */
export const verificarProveedorPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de proveedor es requerido');
    }
    
    console.log(`🔎 Verificando proveedor ID: ${id}`);
    
    const response = await apiFetch(`/api/Proveedor/${id}/exists`);
    
    return {
      success: true,
      existe: response.data || false,
      message: response.message || (response.data ? 'El proveedor existe' : 'Proveedor no existe')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando proveedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo proveedor
 * @param {Object} proveedorData - Datos del proveedor
 * @returns {Promise} Proveedor creado
 */
export const crearProveedor = async (proveedorData) => {
  try {
    // Validar datos requeridos
    const requiredFields = ['nombre', 'rif'];
    const missingFields = requiredFields.filter(field => !proveedorData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
    }
    
    console.log('➕ Creando nuevo proveedor:', proveedorData);
    
    const response = await apiFetch('/api/Proveedor', {
      method: 'POST',
      body: JSON.stringify({
        nombre: toTitleCase(proveedorData.nombre),
        rif: proveedorData.rif,
        telefono: proveedorData.telefono || null,
        email: proveedorData.email || null,
        direccion: proveedorData.direccion || null,
        contacto: toTitleCase(proveedorData.contacto || ''),
        tipoProveedor: proveedorData.tipoProveedor || null,
        especialidad: proveedorData.especialidad || null,
        estado: proveedorData.estado || 'activo'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Proveedor creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando proveedor:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un proveedor existente
 * @param {number} id - ID del proveedor
 * @param {Object} proveedorData - Datos a actualizar
 * @returns {Promise} Proveedor actualizado
 */
export const actualizarProveedor = async (id, proveedorData) => {
  try {
    if (!id) {
      throw new Error('ID de proveedor es requerido');
    }
    
    console.log(`✏️ Actualizando proveedor ID: ${id}`, proveedorData);
    
    const response = await apiFetch(`/api/Proveedor/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        nombre: toTitleCase(proveedorData.nombre),
        rif: proveedorData.rif,
        telefono: proveedorData.telefono || null,
        email: proveedorData.email || null,
        direccion: proveedorData.direccion || null,
        contacto: toTitleCase(proveedorData.contacto || ''),
        tipoProveedor: proveedorData.tipoProveedor || null,
        especialidad: proveedorData.especialidad || null,
        estado: proveedorData.estado || 'activo'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Proveedor actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando proveedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un proveedor
 * @param {number} id - ID del proveedor a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarProveedor = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de proveedor es requerido');
    }
    
    console.log(`🗑️ Eliminando proveedor ID: ${id}`);
    
    const response = await apiFetch(`/api/Proveedor/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Proveedor eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando proveedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Buscar proveedores
 * @param {Object} criterios - Criterios de búsqueda
 * @returns {Promise} Resultados de la búsqueda
 */
export const buscarProveedoresAPI = async (criterios) => {
  try {
    console.log('🔎 Buscando proveedores:', criterios);
    
    const response = await apiFetch('/api/Proveedor/search', {
      method: 'POST',
      body: JSON.stringify(criterios)
    });
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Búsqueda completada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error en búsqueda de proveedores:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener proveedores por tipo
 * @param {string} tipoProveedor - Tipo de proveedor
 * @returns {Promise} Lista de proveedores
 */
export const obtenerProveedoresPorTipo = async (tipoProveedor) => {
  try {
    if (!tipoProveedor) {
      throw new Error('Tipo de proveedor es requerido');
    }
    
    console.log(`📋 Obteniendo proveedores por tipo: ${tipoProveedor}`);
    
    const response = await apiFetch(`/api/Proveedor/tipo/${encodeURIComponent(tipoProveedor)}`);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Proveedores obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo proveedores por tipo ${tipoProveedor}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener proveedores por especialidad
 * @param {string} especialidad - Especialidad
 * @returns {Promise} Lista de proveedores
 */
export const obtenerProveedoresPorEspecialidad = async (especialidad) => {
  try {
    if (!especialidad) {
      throw new Error('Especialidad es requerida');
    }
    
    console.log(`📋 Obteniendo proveedores por especialidad: ${especialidad}`);
    
    const response = await apiFetch(`/api/Proveedor/especialidad/${encodeURIComponent(especialidad)}`);
    
    return {
      success: true,
      data: response.data || response,
      count: Array.isArray(response.data || response) ? (response.data || response).length : 0,
      message: response.message || 'Proveedores obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo proveedores por especialidad ${especialidad}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Cambiar estado de un proveedor (usando PUT)
 * @param {number} id - ID del proveedor
 * @param {string} estado - Nuevo estado
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoProveedor = async (id, estado) => {
  try {
    if (!id || !estado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de proveedor ID: ${id} a ${estado}`);
    
    // Primero obtenemos el proveedor actual
    const proveedorActual = await obtenerProveedorPorId(id);
    
    if (!proveedorActual.success || !proveedorActual.data) {
      throw new Error('No se pudo obtener el proveedor');
    }
    
    // Luego actualizamos solo el estado
    const proveedorData = {
      ...proveedorActual.data,
      estado: estado
    };
    
    // Usamos PUT para actualizar
    return await actualizarProveedor(id, proveedorData);
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de proveedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// Para mantener compatibilidad con el código existente
export const activarProveedor = async (id) => {
  return await cambiarEstadoProveedor(id, 'activo');
};

export const desactivarProveedor = async (id) => {
  return await cambiarEstadoProveedor(id, 'inactivo');
};

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Formatear fecha de proveedor
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaProveedor = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del proveedor
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

/**
 * Formatear RIF
 * @param {string} rif - RIF sin formato
 * @returns {string} RIF formateado
 */
export const formatearRIF = (rif) => {
  if (!rif) return '';
  // Asegurar que tenga formato J-XXXXXXXX-X o similar
  if (rif.length > 2 && !rif.includes('-')) {
    return `${rif.charAt(0)}-${rif.substring(1, rif.length - 1)}-${rif.charAt(rif.length - 1)}`;
  }
  return rif;
};

/**
 * Formatear teléfono
 * @param {string} telefono - Número de teléfono
 * @returns {string} Teléfono formateado
 */
export const formatearTelefono = (telefono) => {
  if (!telefono) return '';
  // Formato: (XXX) XXX-XXXX o similar
  const cleaned = telefono.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  }
  if (cleaned.length === 7) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3)}`;
  }
  return telefono;
};

/**
 * Formatear email
 * @param {string} email - Correo electrónico
 * @returns {string} Email en minúsculas
 */
export const formatearEmail = (email) => {
  if (!email) return '';
  return email.toLowerCase();
};

/**
 * Buscar proveedores por término (filtrado local)
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Proveedores filtrados
 */
export const buscarProveedores = (proveedores, termino) => {
  if (!termino || termino.trim() === '') {
    return proveedores;
  }
  
  const terminoLower = termino.toLowerCase();
  return proveedores.filter(proveedor => 
    proveedor.nombre?.toLowerCase().includes(terminoLower) ||
    proveedor.rif?.toLowerCase().includes(terminoLower) ||
    proveedor.contacto?.toLowerCase().includes(terminoLower) ||
    proveedor.email?.toLowerCase().includes(terminoLower) ||
    proveedor.especialidad?.toLowerCase().includes(terminoLower) ||
    proveedor.tipoProveedor?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(proveedor.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar proveedores por estado (filtrado local)
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} estado - 'all', 'activos', 'inactivos'
 * @returns {Array} Proveedores filtrados
 */
export const filtrarProveedoresPorEstado = (proveedores, estado) => {
  if (estado === 'all') return proveedores;
  if (estado === 'activos') return proveedores.filter(p => p.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return proveedores.filter(p => p.estado?.toLowerCase() === 'inactivo');
  return proveedores;
};

/**
 * Filtrar proveedores por tipo (filtrado local)
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} tipo - Tipo de proveedor
 * @returns {Array} Proveedores filtrados
 */
export const filtrarProveedoresPorTipo = (proveedores, tipo) => {
  if (!tipo || tipo === 'all') return proveedores;
  return proveedores.filter(p => p.tipoProveedor?.toLowerCase() === tipo.toLowerCase());
};

/**
 * Obtener estadísticas de proveedores
 * @param {Array} proveedores - Lista de proveedores
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasProveedores = (proveedores) => {
  const activos = proveedores.filter(p => p.estado?.toLowerCase() === 'activo').length;
  const inactivos = proveedores.filter(p => p.estado?.toLowerCase() === 'inactivo').length;
  
  // Contar por tipo de proveedor
  const tipos = {};
  proveedores.forEach(p => {
    const tipo = p.tipoProveedor || 'Sin tipo';
    tipos[tipo] = (tipos[tipo] || 0) + 1;
  });
  
  // Obtener tipo más común
  let tipoMasComun = 'N/A';
  let maxCount = 0;
  Object.entries(tipos).forEach(([tipo, count]) => {
    if (count > maxCount) {
      maxCount = count;
      tipoMasComun = tipo;
    }
  });
  
  return {
    total: proveedores.length,
    activos,
    inactivos,
    porcentajeActivos: proveedores.length > 0 ? Math.round((activos / proveedores.length) * 100) : 0,
    porcentajeInactivos: proveedores.length > 0 ? Math.round((inactivos / proveedores.length) * 100) : 0,
    tipos,
    tipoMasComun,
    conEmail: proveedores.filter(p => p.email).length,
    conTelefono: proveedores.filter(p => p.telefono).length,
    promedioProductos: proveedores.length > 0 
      ? Math.round(proveedores.reduce((sum, p) => sum + (p.totalProductos || 0), 0) / proveedores.length) 
      : 0,
    promedioCompras: proveedores.length > 0 
      ? Math.round(proveedores.reduce((sum, p) => sum + (p.totalCompras || 0), 0) / proveedores.length) 
      : 0
  };
};

/**
 * Obtener lista de tipos únicos de proveedores
 * @param {Array} proveedores - Lista de proveedores
 * @returns {Array} Tipos únicos
 */
export const obtenerTiposUnicosProveedores = (proveedores) => {
  const tipos = new Set();
  proveedores.forEach(p => {
    if (p.tipoProveedor) {
      tipos.add(p.tipoProveedor);
    }
  });
  return Array.from(tipos).sort();
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar proveedores a Excel
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (proveedores, filename = 'proveedores') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar dinámicamente para evitar problemas de SSR
    const XLSX = require('xlsx');
    
    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Preparar los datos
    const data = proveedores.map(proveedor => ({
      'ID': proveedor.idProveedor || '',
      'Nombre': proveedor.nombre || '',
      'RIF': formatearRIF(proveedor.rif) || '',
      'Teléfono': formatearTelefono(proveedor.telefono) || '',
      'Email': proveedor.email || '',
      'Dirección': proveedor.direccion || '',
      'Contacto': proveedor.contacto || '',
      'Tipo': proveedor.tipoProveedor || '',
      'Especialidad': proveedor.especialidad || '',
      'Estado': convertirEstadoATexto(proveedor.estado),
      'Productos': proveedor.totalProductos || 0,
      'Compras': proveedor.totalCompras || 0,
      'Fecha Registro': formatearFechaProveedor(proveedor.fechaRegistro, true)
    }));
    
    // Convertir a hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Establecer anchos de columna
    const wscols = [
      { wch: 8 },  // ID
      { wch: 35 }, // Nombre
      { wch: 15 }, // RIF
      { wch: 15 }, // Teléfono
      { wch: 30 }, // Email
      { wch: 40 }, // Dirección
      { wch: 25 }, // Contacto
      { wch: 20 }, // Tipo
      { wch: 30 }, // Especialidad
      { wch: 12 }, // Estado
      { wch: 12 }, // Productos
      { wch: 12 }, // Compras
      { wch: 20 }  // Fecha Registro
    ];
    ws['!cols'] = wscols;
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Proveedores');
    
    // Agregar hoja de estadísticas
    const estadisticas = obtenerEstadisticasProveedores(proveedores);
    const statsData = [
      ['ESTADÍSTICAS DE PROVEEDORES', ''],
      ['Total Proveedores', estadisticas.total],
      ['Proveedores Activos', estadisticas.activos],
      ['Proveedores Inactivos', estadisticas.inactivos],
      ['% Activos', `${estadisticas.porcentajeActivos}%`],
      ['% Inactivos', `${estadisticas.porcentajeInactivos}%`],
      ['Tipo Más Común', estadisticas.tipoMasComun],
      ['Con Email', estadisticas.conEmail],
      ['Con Teléfono', estadisticas.conTelefono],
      ['Promedio Productos', estadisticas.promedioProductos],
      ['Promedio Compras', estadisticas.promedioCompras],
      ['', ''],
      ['DISTRIBUCIÓN POR TIPO', 'CANTIDAD']
    ];
    
    // Agregar tipos de proveedores
    Object.entries(estadisticas.tipos).forEach(([tipo, cantidad]) => {
      statsData.push([tipo, cantidad]);
    });
    
    const wsStats = XLSX.utils.aoa_to_sheet(statsData);
    XLSX.utils.book_append_sheet(wb, wsStats, 'Estadísticas');
    
    // Generar nombre de archivo con fecha
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar el archivo
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: proveedores.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar proveedores a PDF
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} title - Título del documento
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (proveedores, title = 'Reporte de Proveedores') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!proveedores || proveedores.length === 0) {
      throw new Error('No hay proveedores para exportar');
    }
    
    // Importar jsPDF y el plugin
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    
    // Crear nuevo documento PDF
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Configuración del documento
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Título del documento
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, 15, { align: 'center' });
    
    // Subtítulo con fecha
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generado: ${fechaStr}`, pageWidth / 2, 22, { align: 'center' });
    
    // Estadísticas
    doc.setFontSize(12);
    doc.text('Resumen Estadístico', 14, 32);
    
    const estadisticas = obtenerEstadisticasProveedores(proveedores);
    const resumen = [
      `Total de proveedores: ${estadisticas.total}`,
      `Proveedores activos: ${estadisticas.activos} (${estadisticas.porcentajeActivos}%)`,
      `Proveedores inactivos: ${estadisticas.inactivos} (${estadisticas.porcentajeInactivos}%)`,
      `Tipo más común: ${estadisticas.tipoMasComun}`,
      `Promedio de productos: ${estadisticas.promedioProductos}`,
      `Promedio de compras: ${estadisticas.promedioCompras}`
    ];
    
    doc.setFontSize(10);
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, 16, 40 + (index * 5));
    });
    
    // Preparar datos para la tabla
    const tableData = proveedores.map(proveedor => [
      proveedor.idProveedor || '',
      proveedor.nombre?.substring(0, 25) + (proveedor.nombre?.length > 25 ? '...' : '') || '',
      formatearRIF(proveedor.rif) || '',
      proveedor.contacto?.substring(0, 20) + (proveedor.contacto?.length > 20 ? '...' : '') || '',
      proveedor.telefono || '',
      proveedor.email?.substring(0, 20) + (proveedor.email?.length > 20 ? '...' : '') || '',
      proveedor.tipoProveedor?.substring(0, 15) || '',
      convertirEstadoATexto(proveedor.estado),
      proveedor.totalProductos || 0,
      proveedor.totalCompras || 0
    ]);
    
    // Crear tabla
    doc.autoTable({
      startY: 70,
      head: [['ID', 'Nombre', 'RIF', 'Contacto', 'Teléfono', 'Email', 'Tipo', 'Estado', 'Productos', 'Compras']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [52, 152, 219],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 35 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 30 },
        6: { cellWidth: 20 },
        7: { cellWidth: 15 },
        8: { cellWidth: 15 },
        9: { cellWidth: 15 }
      },
      margin: { top: 70 },
      styles: {
        fontSize: 8,
        cellPadding: 3
      },
      didDrawPage: function(data) {
        // Pie de página
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Página ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          'SYSTEM JJ',
          pageWidth - 10,
          pageHeight - 10,
          { align: 'right' }
        );
      }
    });
    
    // Generar nombre de archivo con fecha
    const fechaArchivo = fecha.toISOString().split('T')[0];
    const nombreArchivo = `reporte_proveedores_${fechaArchivo}.pdf`;
    
    // Guardar el archivo
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Documento ${nombreArchivo} generado exitosamente`,
      count: proveedores.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} proveedores - Lista de proveedores
 * @param {string} baseFilename - Nombre base del archivo (sin extensión)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompleto = async (proveedores, baseFilename = 'reporte_completo_proveedores') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (proveedores.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    // Exportar Excel
    const excelResult = exportarExcel(proveedores, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    // Pequeño delay para evitar conflictos
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Exportar PDF
    const pdfResult = exportarPDF(proveedores, `Reporte de Proveedores - ${baseFilename}`);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: proveedores.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerProveedores,
  obtenerProveedorPorId,
  obtenerProveedorConRelaciones,
  verificarRifProveedor,
  verificarProveedorPorId,
  crearProveedor,
  actualizarProveedor,
  eliminarProveedor,
  buscarProveedoresAPI,
  obtenerProveedoresPorTipo,
  obtenerProveedoresPorEspecialidad,
  activarProveedor,
  desactivarProveedor,
  cambiarEstadoProveedor,
  
  // Funciones auxiliares
  formatearFechaProveedor,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearRIF,
  formatearTelefono,
  formatearEmail,
  buscarProveedores,
  filtrarProveedoresPorEstado,
  filtrarProveedoresPorTipo,
  obtenerEstadisticasProveedores,
  obtenerTiposUnicosProveedores,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
};